import React, { useState, useEffect, useMemo, useCallback } from "react";
import StatusPill from "../components/common/StatusPill";
import { motion } from "framer-motion";
import {
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Search,
  User,
  Receipt,
  Clock,
  CheckCircle,
  XCircle,
  Wallet,
  Settings2,
  Banknote,
  Hourglass,
  CalendarDays,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import StatStrip from "../components/common/StatStrip";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { loadLedger, saveFinanceSettings } from "../services/financeService";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDate = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : "—";

const STATUS = {
  completed: { label: "Completed", tone: "green", icon: CheckCircle },
  held: { label: "Awaiting consultation", tone: "amber", icon: Hourglass },
  cancelled: { label: "Cancelled", tone: "red", icon: XCircle },
  paid: { label: "Paid out", tone: "green", icon: CheckCircle },
  pending: { label: "Pending", tone: "amber", icon: Clock },
  rejected: { label: "Rejected", tone: "red", icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <StatusPill tone={s.tone} icon={s.icon}>{s.label}</StatusPill>;
};

const TypeBadge = ({ type }) =>
  type === "credit" ? (
    <StatusPill tone="green" icon={ArrowDownLeft}>Credit</StatusPill>
  ) : (
    <StatusPill tone="red" icon={ArrowUpRight}>Withdrawal</StatusPill>
  );

const Row = ({ label, value, strong, mono, negative }) => (
  <div
    style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "9px 14px",
      borderTop: "1px solid hsl(var(--border))",
      background: strong ? "linear-gradient(90deg, hsl(var(--accent) / 0.9), hsl(var(--accent) / 0.2))" : "transparent",
    }}
  >
    <span style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>{label}</span>
    <span
      className={mono ? "num" : undefined}
      style={{ fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 500, color: negative ? "hsl(var(--on-red))" : "hsl(var(--foreground))", textAlign: "right", wordBreak: "break-all" }}
    >
      {value || "—"}
    </span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div style={{ marginTop: 12, borderRadius: 14, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", overflow: "hidden" }}>
    <p style={{ margin: 0, padding: "9px 14px", display: "flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 700, color: "hsl(var(--primary))", letterSpacing: "0.6px", textTransform: "uppercase", background: "hsl(var(--muted) / 0.6)" }}>
      {Icon && <Icon size={13} />} {title}
    </p>
    {children}
  </div>
);

const TransactionViewer = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ledger, setLedger] = useState({ entries: [], balances: [], settings: null, truncated: false });
  const [tab, setTab] = useState("ledger");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDoctor, setFilterDoctor] = useState("all");
  const [selected, setSelected] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState({ platformFee: "50", doctorCommissionPercent: "0" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await loadLedger();
      setLedger(data);
      setForm({
        platformFee: String(data.settings.platformFee),
        doctorCommissionPercent: String(data.settings.doctorCommissionPercent),
      });
    } catch (e) {
      setError(e?.message || "Couldn't load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totals = useMemo(() => {
    const t = { credited: 0, completed: 0, held: 0, withdrawn: 0, pendingWithdrawal: 0, available: 0 };
    ledger.balances.forEach((b) => {
      t.credited += b.credited;
      t.completed += b.completed;
      t.held += b.held;
      t.withdrawn += b.withdrawn;
      t.pendingWithdrawal += b.pendingWithdrawal;
      t.available += b.available;
    });
    return t;
  }, [ledger.balances]);

  const doctors = useMemo(() => {
    const m = new Map();
    ledger.entries.forEach((e) => m.set(e.doctorId || e.doctorName, e.doctorName));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [ledger.entries]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return ledger.entries.filter((e) => {
      if (filterType !== "all" && e.type !== filterType) return false;
      if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterDoctor !== "all" && (e.doctorId || e.doctorName) !== filterDoctor) return false;
      if (!q) return true;
      return [e.id, e.doctorName, e.patientName, e.patientPhone, e.paymentId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [ledger.entries, searchTerm, filterType, filterStatus, filterDoctor]);

  const doctorBalance = (e) =>
    ledger.balances.find((b) => (b.doctorId || b.doctorName) === (e.doctorId || e.doctorName));

  const onSave = async () => {
    setSaving(true);
    setFormError("");
    try {
      await saveFinanceSettings({
        platformFee: form.platformFee,
        doctorCommissionPercent: form.doctorCommissionPercent,
      });
      setSettingsOpen(false);
      await refresh();
    } catch (e) {
      setFormError(e?.message || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner fullHeight message="Loading transactions..." />;

  const card = { padding: "12px 14px", position: "relative", overflow: "hidden" };
  const th = { padding: "11px 14px", fontSize: 10.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", whiteSpace: "nowrap" };
  const td = { padding: "12px 14px", fontSize: 12.5, color: "hsl(var(--foreground))" };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#8b5cf6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Receipt size={14} color="white" />
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "hsl(var(--foreground))", letterSpacing: "-0.4px" }}>Transactions & Withdrawals</h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
            Booking credits and withdrawals in one ledger, with what each doctor can withdraw
          </p>
        </div>
        <Button variant="outline" onClick={() => setSettingsOpen(true)} style={{ height: 36, gap: 6 }}>
          <Settings2 size={14} /> Finance settings
          {ledger.settings && (
            <span style={{ color: "hsl(var(--muted-foreground))", fontWeight: 500 }}>
              · fee {inr(ledger.settings.platformFee)} · commission {ledger.settings.doctorCommissionPercent}%
            </span>
          )}
        </Button>
      </div>

      {error && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: 10, background: "hsl(var(--tint-red))", border: "1px solid hsl(var(--tint-red-border))", color: "hsl(var(--on-red))", fontSize: 13 }}>
          {error}{" "}
          <button onClick={refresh} style={{ textDecoration: "underline", fontWeight: 700 }}>Retry</button>
        </div>
      )}
      {ledger.truncated && (
        <div style={{ padding: "8px 14px", borderRadius: 10, background: "hsl(var(--tint-amber))", border: "1px solid hsl(var(--tint-amber-border))", color: "hsl(var(--on-amber))", fontSize: 12.5 }}>
          Showing the latest 1000 bookings only — older ones aren't included in these totals.
        </div>
      )}

      {/* Stats */}
      <StatStrip
        items={[
          { label: "Total credited", value: inr(totals.credited), icon: ArrowDownLeft, tone: "green", hint: "All paid bookings" },
          { label: "Awaiting consultation", value: inr(totals.held), icon: Hourglass, tone: "amber", hint: "Held until completed" },
          { label: "Withdrawn", value: inr(totals.withdrawn), icon: Banknote, tone: "red", hint: "Paid out to doctors" },
          { label: "Withdrawal pending", value: inr(totals.pendingWithdrawal), icon: Clock, tone: "blue", hint: "Requested, not paid" },
          { label: "Available to withdraw", value: inr(totals.available), icon: Wallet, tone: "violet", hint: "Ready for payout" },
        ]}
      />

      {/* Tabs */}
      <div style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 12, background: "hsl(var(--muted))", border: "1px solid hsl(var(--border))" }}>
        {[
          ["ledger", "Ledger"],
          ["balances", "Doctor balances"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              padding: "7px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", transition: "all .15s",
              background: tab === k ? "linear-gradient(180deg, hsl(217 71% 54%), hsl(222 72% 45%))" : "transparent",
              color: tab === k ? "white" : "hsl(var(--muted-foreground))",
              boxShadow: tab === k ? "0 2px 8px hsl(217 71% 49% / 0.35)" : "none",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "ledger" ? (
        <>
          <div className="admin-card" style={{ padding: "14px 18px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))" }} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search doctor, patient, phone or payment ID…"
                style={{ width: "100%", paddingLeft: 30, paddingRight: 12, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none" }}
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger style={{ width: 150, height: 36 }}><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="credit">Credits</SelectItem>
                <SelectItem value="withdrawal">Withdrawals</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger style={{ width: 170, height: 36 }}><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                {Object.entries(STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterDoctor} onValueChange={setFilterDoctor}>
              <SelectTrigger style={{ width: 190, height: 36 }}><SelectValue placeholder="Doctor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All doctors</SelectItem>
                {doctors.map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <CreditCard size={40} style={{ color: "hsl(var(--input))", margin: "0 auto 12px" }} />
                <p style={{ fontSize: 15, fontWeight: 600, color: "hsl(var(--foreground))" }}>No transactions found</p>
                <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Paid bookings and withdrawals appear here</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1.5px solid hsl(var(--border))" }}>
                      {["Date", "Type", "Doctor", "Patient", "Amount", "Status"].map((h) => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e, idx) => (
                      <tr
                        key={`${e.type}-${e.id}`}
                        onClick={() => setSelected(e)}
                        style={{ borderBottom: "1px solid hsl(var(--muted))", background: idx % 2 ? "hsl(var(--muted) / 0.4)" : "hsl(var(--card))", cursor: "pointer" }}
                        onMouseEnter={(ev) => (ev.currentTarget.style.background = "hsl(var(--tint-violet))")}
                        onMouseLeave={(ev) => (ev.currentTarget.style.background = idx % 2 ? "hsl(var(--muted) / 0.4)" : "hsl(var(--card))")}
                      >
                        <td className="num" style={td}>{fmtDate(e.date)}</td>
                        <td style={td}><TypeBadge type={e.type} /></td>
                        <td style={{ ...td, fontWeight: 600, color: "hsl(var(--foreground))" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <User size={13} color="#6366f1" /> {e.doctorName}
                          </span>
                        </td>
                        <td style={td}>{e.type === "credit" ? e.patientName : "—"}</td>
                        <td style={{ ...td, fontWeight: 700, color: e.type === "credit" ? "#10b981" : "#ef4444" }}>
                          {e.type === "credit" ? "+" : "−"}
                          {inr(e.amount)}
                        </td>
                        <td style={td}><StatusBadge status={e.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1.5px solid hsl(var(--border))" }}>
                  {["Doctor", "Credited", "Awaiting consultation", "Withdrawn", "Withdrawal pending", "Available to withdraw"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.balances.map((b, idx) => (
                  <tr
                    key={b.doctorId || b.doctorName}
                    onClick={() => {
                      setFilterDoctor(b.doctorId || b.doctorName);
                      setTab("ledger");
                    }}
                    style={{ borderBottom: "1px solid hsl(var(--muted))", background: idx % 2 ? "hsl(var(--muted) / 0.4)" : "hsl(var(--card))", cursor: "pointer" }}
                  >
                    <td style={{ ...td, fontWeight: 600, color: "hsl(var(--foreground))" }}>{b.doctorName}</td>
                    <td className="num" style={td}>{inr(b.credited)}</td>
                    <td className="num" style={td}>{inr(b.held)}</td>
                    <td className="num" style={td}>{inr(b.withdrawn)}</td>
                    <td className="num" style={td}>{inr(b.pendingWithdrawal)}</td>
                    <td className="num" style={{ ...td, fontWeight: 600, color: "#7c3aed" }}>{inr(b.available)}</td>
                  </tr>
                ))}
                {ledger.balances.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 40 }}>No doctor balances yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction popup */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent style={{ maxWidth: 500, maxHeight: "88vh", overflowY: "auto" }}>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {selected.type === "credit" ? "Booking credit" : "Withdrawal"}
                </DialogTitle>
                <DialogDescription>{fmtDate(selected.date)}</DialogDescription>
              </DialogHeader>

              <div
                style={{
                  textAlign: "center", padding: "18px 12px 16px", borderRadius: 16,
                  border: `1px solid hsl(var(${selected.type === "credit" ? "--tint-green-border" : "--tint-red-border"}))`,
                  background: `radial-gradient(120% 120% at 50% 0%, hsl(var(${selected.type === "credit" ? "--tint-green" : "--tint-red"})), transparent 75%)`,
                }}
              >
                <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.6px", textTransform: "uppercase", color: "hsl(var(--muted-foreground))" }}>
                  {selected.type === "credit" ? "Credited to doctor" : "Withdrawal amount"}
                </p>
                <p className="num" style={{ margin: "4px 0 0", fontSize: 34, lineHeight: 1.1, fontWeight: 600, color: selected.type === "credit" ? "hsl(var(--on-green))" : "hsl(var(--on-red))" }}>
                  {selected.type === "credit" ? "+" : "−"}
                  {inr(selected.amount)}
                </p>
                <div style={{ marginTop: 10 }}><StatusBadge status={selected.status} /></div>
              </div>

              {selected.type === "credit" ? (
                <>
                  <Section title="Patient" icon={User}>
                    <Row label="Name" value={selected.patientName} strong />
                    <Row label="Age / gender" value={[selected.patientAge, selected.patientGender].filter(Boolean).join(" · ")} />
                    <Row label="Booked for" value={selected.patientRelation} />
                    <Row mono label="WhatsApp" value={selected.patientPhone} />
                  </Section>
                  <Section title="Booking" icon={CalendarDays}>
                    <Row label="Doctor" value={selected.doctorName} strong />
                    <Row mono label="Booked on" value={fmtDate(selected.date)} />
                    <Row mono label="Consultation time" value={fmtDate(selected.consultationTime)} />
                    <Row mono label="Booking ID" value={selected.id} />
                  </Section>
                  <Section title="Amount breakdown" icon={Receipt}>
                    <Row mono label="Paid by patient" value={inr(selected.amountPaid)} />
                    {selected.couponCode && (
                      <Row mono negative label={`Coupon ${selected.couponCode}`} value={`−${inr(selected.couponDiscount)}`} />
                    )}
                    <Row mono negative label="Platform fee" value={`−${inr(selected.platformFee)}`} />
                    <Row mono negative label={`Commission (${selected.commissionPercent}%)`} value={`−${inr(selected.commission)}`} />
                    <Row mono label="Credited to doctor" value={inr(selected.amount)} strong />
                  </Section>
                  <Section title="Payment" icon={CreditCard}>
                    <Row mono label="Payment ID" value={selected.paymentId} />
                    <Row mono label="Order ID" value={selected.orderId} />
                    <Row label="Via" value={selected.paymentVia} />
                  </Section>
                </>
              ) : (
                <Section title="Withdrawal" icon={Banknote}>
                  <Row label="Doctor" value={selected.doctorName} strong />
                  <Row mono label="Requested" value={fmtDate(selected.date)} />
                  <Row mono label="Processed" value={selected.processedAt ? fmtDate(selected.processedAt) : ""} />
                  <Row label="Method" value={selected.method} />
                  <Row mono label="Destination" value={selected.destination} />
                  <Row label="Note" value={selected.note} />
                </Section>
              )}

              {(() => {
                const b = doctorBalance(selected);
                return b ? (
                  <Section title={`${selected.doctorName} — balance`} icon={Wallet}>
                    <Row mono label="Available to withdraw" value={inr(b.available)} strong />
                    <Row mono label="Awaiting consultation" value={inr(b.held)} />
                    <Row mono label="Withdrawn / pending" value={`${inr(b.withdrawn)} / ${inr(b.pendingWithdrawal)}`} />
                  </Section>
                ) : null;
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Finance settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent style={{ maxWidth: 420 }}>
          <DialogHeader>
            <DialogTitle>Finance settings</DialogTitle>
            <DialogDescription>
              Applies to new bookings. Existing bookings keep the platform fee they were booked with.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
            <div>
              <Label htmlFor="platform-fee">Platform fee (₹, added to the doctor's fee)</Label>
              <Input
                id="platform-fee"
                inputMode="numeric"
                value={form.platformFee}
                onChange={(e) => setForm({ ...form, platformFee: e.target.value.replace(/[^\d]/g, "") })}
              />
            </div>
            <div>
              <Label htmlFor="commission">Doctor commission (% deducted from the doctor's share)</Label>
              <Input
                id="commission"
                inputMode="decimal"
                value={form.doctorCommissionPercent}
                onChange={(e) => setForm({ ...form, doctorCommissionPercent: e.target.value.replace(/[^\d.]/g, "") })}
              />
            </div>
            {formError && <p style={{ margin: 0, color: "hsl(var(--on-red))", fontSize: 12.5 }}>{formError}</p>}
            <Button onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionViewer;
